import React, { useEffect, useState } from "react";
import { Alert, Button, Collapse, FormGroup, Input, Label, Spinner } from "reactstrap";
import { Redirect } from "react-router-dom";
import MyCollectionLayout from "./MyCollectionLayout";
import McBackLink from "./McBackLink";
import { getCollectorMe, requestPremiumAccess } from "../../services/collectorApi";
import { collectorLoginPath, getCollectorToken } from "../../helpers/collectorSession";

const TOP_BENEFITS = [
  "Private QR pages with an account passkey",
  "Keep every story, photo, and sample — nothing is deleted when a trial ends",
];

const MORE_BENEFITS = [
  "Media gallery on journal pages (coming with premium)",
  "PDF export of your collection (coming with premium)",
  "AI-assisted story polish (coming with premium)",
];

const UpgradePage: React.FC = () => {
  const hasToken = Boolean(getCollectorToken());
  const [loading, setLoading] = useState(hasToken);
  const [showMore, setShowMore] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [account, setAccount] = useState<{
    phone?: string;
    email?: string;
    plan?: string;
    trialEndsAt?: string;
  }>({});
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!hasToken) return;
    getCollectorMe()
      .then((res) => {
        const data = res.data?.data || {};
        setAccount(data);
        setEmail(data.email || "");
      })
      .catch(() => setError("Could not load your account"))
      .finally(() => setLoading(false));
  }, [hasToken]);

  if (!hasToken) {
    return <Redirect to={collectorLoginPath("/my-collection/upgrade")} />;
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!email.trim() || !email.includes("@")) {
      setError("Email is required to request premium access. Add it here or in Settings.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await requestPremiumAccess(email.trim(), message.trim() || undefined);
      const data = res.data?.data || {};
      setSuccess(data.message || "Trial extended while we review your request");
      setAccount((prev) => ({ ...prev, plan: data.plan || "TRIAL", trialEndsAt: data.trialEndsAt }));
    } catch (err: any) {
      setError(err?.response?.data?.message || "Could not submit your request");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <MyCollectionLayout>
        <div className="text-center py-5"><Spinner /></div>
      </MyCollectionLayout>
    );
  }

  return (
    <MyCollectionLayout>
      <McBackLink to="/my-collection">Back to My pieces</McBackLink>
      <h1 className="h3 mb-1">Upgrade My Collection</h1>
      <p className="text-muted">
        Current plan: <strong>{account.plan || "FREE"}</strong>
        {account.trialEndsAt && account.plan === "TRIAL" && (
          <span> · trial until {new Date(account.trialEndsAt).toLocaleDateString()}</span>
        )}
      </p>

      <div className="mc-settings-grid">
        <section className="mc-card">
          <h2 className="h6 text-uppercase text-muted mb-3">Why premium</h2>
          <ol className="ps-3 mb-3">
            {TOP_BENEFITS.map((b) => (
              <li key={b} className="mb-2">{b}</li>
            ))}
          </ol>
          <button
            type="button"
            className="mc-btn-ghost"
            onClick={() => setShowMore(!showMore)}
          >
            {showMore ? "Hide extra features" : "See all features"}
          </button>
          <Collapse isOpen={showMore}>
            <ul className="mt-2 mb-0 ps-3 text-muted">
              {MORE_BENEFITS.map((b) => (
                <li key={b} className="mb-1">{b}</li>
              ))}
            </ul>
          </Collapse>
        </section>

        <section className="mc-card">
          <h2 className="h6 text-uppercase text-muted mb-2">Request premium access</h2>
          <p className="small text-muted">
            During the pilot we review requests by hand. Submitting extends your trial by 30 days while we look at it.
          </p>
          {error && <Alert color="danger">{error}</Alert>}
          {success && <Alert color="success">{success}</Alert>}
          <form onSubmit={submit}>
            <FormGroup>
              <Label>Phone</Label>
              <Input value={account.phone || ""} disabled />
            </FormGroup>
            <FormGroup>
              <Label for="upgradeEmail">Email <span className="mc-required">*</span></Label>
              <Input
                id="upgradeEmail"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
              />
              {error.includes("Email is required") && <div className="mc-field-error">{error}</div>}
              <small className="text-muted">Required before we can review your request.</small>
            </FormGroup>
            <FormGroup>
              <Label for="upgradeMessage">Message (optional)</Label>
              <Input
                id="upgradeMessage"
                type="textarea"
                rows={3}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Anything we should know?"
              />
            </FormGroup>
            <Button className="mc-btn-primary" type="submit" disabled={submitting}>
              {submitting ? "Sending…" : "Request premium access"}
            </Button>
          </form>
        </section>
      </div>
    </MyCollectionLayout>
  );
};

export default UpgradePage;
